import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { PricingPage } from "@/pages/PricingPage";
import { FAQPage } from "@/pages/FAQPage";
import { 
  Menu, 
  X, 
  ArrowRight, 
  CheckCircle2, 
  TrendingUp, 
  Zap, 
  Shield, 
  BarChart3, 
  Clock, 
  CreditCard, 
  Users, 
  FileText,
  RefreshCw,
  ChevronDown,
  Package,
  DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [benefitsTab, setBenefitsTab] = useState("distributors");
  const [howItWorksTab, setHowItWorksTab] = useState("distributors");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  // Hero image carousel
  // const heroImages = [
  //   "https://images.pexels.com/photos/27111449/pexels-photo-27111449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  //   "https://images.unsplash.com/photo-1739066598279-1297113f5c6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzaG9wa2VlcGVyJTIwc21pbGluZyUyMGluJTIwbW9kZXJuJTIwa2lyYW5hJTIwc3RvcmUlMjBjb3VudGVyfGVufDB8fHx8MTc3MDIyNjI5MXww&ixlib=rb-4.1.0&q=85",
  //   "https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  // ];

const heroImages = [
  "/images/cashengine.png",
   "/images/distributor.png",
  "/images/retailer.png"
 
];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    console.log("Demo request:", formData);
    toast.success("Demo request submitted successfully! We'll contact you soon.");
    setDemoDialogOpen(false);
    setFormData({ name: "", email: "", phone: "", company: "", message: "" });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <a href="#" data-testid="logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fintech-demo-9/artifacts/hrowc68u_image.png" 
                  alt="TurboPay" 
                  className="h-8 md:h-10 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8 text-lg">
              {/* <button onClick={() => scrollToSection('solution')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-product">
                Product
              </button> */}
              <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-solutions">
                Benefits
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-how-it-works">
                How it Works
              </button>
              <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-customers">
                Customers
              </button>
              <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-faq">
                FAQs
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-11 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="nav-get-demo-btn">
                    Get Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]" data-testid="demo-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                    <DialogDescription>
                      Fill out the form below and we'll get back to you shortly.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input 
                        id="name" 
                        name="name" 
                        required 
                        value={formData.name}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        required 
                        value={formData.email}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input 
                        id="phone" 
                        name="phone" 
                        type="tel" 
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input 
                        id="company" 
                        name="company" 
                        value={formData.company}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-company-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea 
                        id="message" 
                        name="message" 
                        rows={3}
                        value={formData.message}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-message-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold" data-testid="demo-form-submit-btn">
                      Book Demo
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
              <div className="flex flex-col space-y-3">
                {/* <button onClick={() => scrollToSection('solution')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Product
                </button> */}
                <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Benefits
                </button>
                <button onClick={() => scrollToSection('how-it-works')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  How it Works
                </button>
                <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Pricing
                </Link>
                <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Customers
                </button>
                <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  FAQs
                </Link>
                <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#1F497D] text-white hover:bg-[#183a64] mt-4" data-testid="mobile-get-demo-btn">
                      Get Demo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                      <DialogDescription>
                        Fill out the form below and we'll get back to you shortly.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="mobile-name">Name *</Label>
                        <Input 
                          id="mobile-name" 
                          name="name" 
                          required 
                          value={formData.name}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-email">Email *</Label>
                        <Input 
                          id="mobile-email" 
                          name="email" 
                          type="email" 
                          required 
                          value={formData.email}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-phone">Phone</Label>
                        <Input 
                          id="mobile-phone" 
                          name="phone" 
                          type="tel" 
                          value={formData.phone}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-company">Company</Label>
                        <Input 
                          id="mobile-company" 
                          name="company" 
                          value={formData.company}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-message">Message</Label>
                        <Textarea 
                          id="mobile-message" 
                          name="message" 
                          rows={3}
                          value={formData.message}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold">
                        Book Demo
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient py-8 md:py-12 pb-16 md:pb-16" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <h1 className="font-outfit text-4xl md:text-6xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900" data-testid="hero-heading">
                {/* Collect Faster. Fund Smarter. Grow Without Limits. */}
                The Cash Flow Engine for Distributors and Retailers
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-testid="hero-subheading">
                TurboPay helps FMCG distributors collect faster and provides instant working capital to both distributors and retailers — all in one platform.
              </p>
              <p className="text-base md:text-lg text-[#1F497D] font-medium" data-testid="hero-supporting-text">
                Digitize collections. Prevent cash leakages. Unlock working capital.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-12 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="hero-book-demo-btn">
                      Book a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                </Dialog>
                <Button 
                  variant="outline" 
                  className="bg-white text-slate-900 border border-slate-200 hover:border-[#1F497D] hover:bg-blue-50/50 h-12 px-8 rounded-full font-medium transition-all hover:shadow-sm active:scale-95"
                  onClick={() => scrollToSection('how-it-works')}
                  data-testid="hero-see-how-it-works-btn"
                >
                  See How It Works
                </Button>
              </div>
            </div>

            {/* Right Column - Image Carousel */}
            <div className="relative h-[300px] md:h-[350px] lg:h-[400px]" data-testid="hero-visual">
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
                {heroImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img 
                      src={image}
                      alt={`FMCG business ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              {/* Carousel indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-white w-8' 
                        : 'bg-white/50'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Hidden */}
      <section className="hidden py-16 md:py-20 border-y border-slate-100 bg-slate-50" data-testid="trust-bar">
        <div className="container-custom">
          <p className="text-center text-sm font-medium text-slate-600 mb-12" data-testid="trust-bar-text">
            Trusted by growing FMCG distributors and retailers across India
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16 grayscale opacity-60">
            {/* Dummy Company Logos */}
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">COMPANY</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">BRAND</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">PARTNER</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">CLIENT</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">CUSTOMER</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">ENTERPRISE</span>
            </div>
          </div>
        </div>
      </section>


      {/* Unified Features Section */}
      <section id="features" className="py-10 md:py-12 bg-slate-50 scroll-mt-24" data-testid="features-section">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto text-center mb-12">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-slate-900 mb-6" data-testid="features-heading">
              How TurboPay Helps
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-testid="features-subheading">
             From collections to capital — we handle your entire cash cycle. <br />
Built to help distributors collect faster and give both distributors and retailers easy access to working capital.

            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-1">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Real-Time Invoice Visibility</h3>
              <p className="text-slate-600 leading-relaxed">Track every invoice and payment live, so you always know outstanding amounts and expected collections.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-2">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <CreditCard className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">100% Digital Collection Recording</h3>
              <p className="text-slate-600 leading-relaxed">Capture every distributor payment digitally with accurate timestamps, eliminating errors, disputes, and untracked cash leakages.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-3">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Field Collection Accountability</h3>
              <p className="text-slate-600 leading-relaxed">Monitor beat-wise collections with OTP confirmations and geo-tracking to ensure every visit and payment is verified.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">On-Demand Working Capital</h3>
              <p className="text-slate-600 leading-relaxed">Access instant working capital whenever cash flow tightens, helping you restock inventory and maintain steady growth.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-5">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Retailer Credit Enablement</h3>
              <p className="text-slate-600 leading-relaxed">Offer retailers easy short-term credit, enabling larger purchases, faster inventory turns, and stronger long-term relationships.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-6">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <RefreshCw className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Automated Reconciliation</h3>
              <p className="text-slate-600 leading-relaxed">Automatically match invoices, collections, and settlements, removing manual accounting work and reducing costly errors.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-7">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Complete Cash Flow Control</h3>
              <p className="text-slate-600 leading-relaxed">Get a single view of every rupee moving through your business, across collections, credit, and payouts.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-8">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Actionable Reports & Insights</h3>
              <p className="text-slate-600 leading-relaxed">Daily dashboards and analytics highlight delays, risks, and opportunities, helping you make faster, smarter decisions.</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-9">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Paperless, End-to-End Operations</h3>
              <p className="text-slate-600 leading-relaxed">Digitize the entire collections and funding process, eliminating paperwork, manual follow-ups, and operational bottlenecks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section with Tabs */}
      <section className="py-10 md:py-16 bg-slate-50" data-testid="benefits-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">Built for Distributors and Retailers</h2>
            
            {/* Tab Toggle */}
            <div className="inline-flex items-center p-1 bg-white rounded-full shadow-sm border border-slate-200">
              <button
                onClick={() => setBenefitsTab("distributors")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  benefitsTab === "distributors"
                    ? "bg-[#1F497D] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="benefits-distributors-tab"
              >
                Distributors
              </button>
              <button
                onClick={() => setBenefitsTab("retailers")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  benefitsTab === "retailers"
                    ? "bg-[#1F497D] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="benefits-retailers-tab"
              >
                Retailers
              </button>
            </div>
          </div>

          {/* Distributors Content */}
          {benefitsTab === "distributors" && (
            <div className="transition-all duration-300" data-testid="benefits-distributors">
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Streamline operations and unlock growth with powerful tools designed for your business
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Faster collections</h4>
                  <p className="text-slate-600 text-base">Shorten collection cycles and receive payments sooner, improving liquidity and keeping your cash flow consistently healthy.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Cashless, Secure Payments</h4>
                  <p className="text-slate-600 text-base">Reduce cash handling risks with fully digital tracking, ensuring safer collections and eliminating losses or disputes.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Retailer Credit Enablement</h4>
                  <p className="text-slate-600 text-base">Offer instant credit to retailers, enabling larger orders, faster inventory movement, and higher secondary sales.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Field Team Accountability</h4>
                  <p className="text-slate-600 text-base">Track beat performance, visits, and collections in real time to improve productivity and eliminate follow-up gaps.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Predictable cash flow</h4>
                  <p className="text-slate-600 text-base">Forecast inflows accurately with real-time data, helping you plan purchases, payouts, and growth confidently</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Instant Working Capital Access</h4>
                  <p className="text-slate-600 text-base">Unlock on-demand funding against invoices to bridge cash gaps and maintain uninterrupted business operations.</p>
                </div>
              </div>
            </div>
          )}

          {/* Retailers Content */}
          {benefitsTab === "retailers" && (
            <div className="transition-all duration-300" data-testid="benefits-retailers">
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Access credit easily and grow your business without cash constraints
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Buy inventory even when cash is tight</h4>
                  <p className="text-slate-600 text-base">Stock shelves even when cash is tight, ensuring you never miss sales due to inventory shortages.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Quick short-term credit</h4>
                  <p className="text-slate-600 text-base">Get approved quickly and receive funds within hours, so you can respond immediately to demand.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No collateral required</h4>
                  <p className="text-slate-600 text-base">Access working capital without pledging property or assets, making credit simple and stress-free.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <RefreshCw className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Flexible, Easy Repayments</h4>
                  <p className="text-slate-600 text-base">Repay in convenient schedules aligned with your sales cycle, reducing pressure on daily cash flow.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Grow your business confidently</h4>
                  <p className="text-slate-600 text-base">Increase stock variety and order sizes with reliable funding that supports consistent business expansion.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Simple Digital Experience</h4>
                  <p className="text-slate-600 text-base">Apply, track loans, and repay digitally through one app, with zero paperwork or branch visits.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works with Tabs */}
      <section id="how-it-works" className="py-10 md:py-16 scroll-mt-24" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">How TurboPay Works</h2>
            
            {/* Tab Toggle */}
            <div className="inline-flex items-center p-1 bg-white rounded-full shadow-sm border border-slate-200">
              <button
                onClick={() => setHowItWorksTab("distributors")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  howItWorksTab === "distributors"
                    ? "bg-[#1F497D] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="how-it-works-distributors-tab"
              >
                Distributors
              </button>
              <button
                onClick={() => setHowItWorksTab("retailers")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  howItWorksTab === "retailers"
                    ? "bg-[#1F497D] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="how-it-works-retailers-tab"
              >
                Retailers
              </button>
            </div>
          </div>

          {/* Distributors Steps */}
          {howItWorksTab === "distributors" && (
            <div className="transition-all duration-300">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Package className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 1</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Upload invoices</h4>
                  <p className="text-base text-slate-600">Distributors upload or sync invoices instantly.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <DollarSign className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 2</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Request Funding</h4>
                  <p className="text-base text-slate-600">Distributor selects invoices for discounting.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 3</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Record real time</h4>
                  <p className="text-base text-slate-600">Collections recorded via TurboPay in real time.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 4</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Digital collections</h4>
                  <p className="text-base text-slate-600">Digital payments settle directly into TurboPay</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-5">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 5</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Cash Collections</h4>
                  <p className="text-base text-slate-600">Distributor transfers cash collections to TurboPay.</p>
                </div>
              </div>
            </div>
          )}

          {/* Retailers Steps */}
          {howItWorksTab === "retailers" && (
            <div className="transition-all duration-300">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Package className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 1</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Upload invoices</h4>
                  <p className="text-base text-slate-600">Distributors upload or sync invoices instantly.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <DollarSign className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 2</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Request Funding</h4>
                  <p className="text-base text-slate-600">Retailer selects distributor invoices for funding</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 3</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Disburse funds</h4>
                  <p className="text-base text-slate-600">TurboPay disburses funds to distributors directly.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 4</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Digital collections</h4>
                  <p className="text-base text-slate-600">Retailer pays digitally on due date through TurboPay App</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-5">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 5</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Cash Collections</h4>
                  <p className="text-base text-slate-600">Agents collect cash on due dates & deposit into the bank.</p>
                </div>
              </div>
              
            </div>
          )}
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-10 md:py-16 bg-slate-50" data-testid="stats-section">
        <div className="container-custom">
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-16 text-slate-900" data-testid="stats-heading">
            Real Business Impact
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="stat-1">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">10</div>
              <p className="text-slate-700 font-medium">FMCG Distributors on TurboPay</p>
            </div>

            <div className="text-center" data-testid="stat-2">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">11,000+</div>
              <p className="text-slate-700 font-medium">Retailers on TurboPay</p>
            </div>

            <div className="text-center" data-testid="stat-3">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">60,000+</div>
              <p className="text-slate-700 font-medium">Invoices Collected</p>
            </div>

            <div className="text-center" data-testid="stat-4">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">₹10 Crore</div>
              <p className="text-slate-700 font-medium">Collections Processed</p>
            </div>
          </div>
          <br />
          <br />
            <p className="text-center text-xl text-slate-600 max-w-5xl mx-auto mb-6">
                Launched recently and already powering thousands of daily collections across FMCG supply chains.
              </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-10 md:py-16 scroll-mt-24" data-testid="testimonials-section">
        <div className="container-custom">
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-12 text-slate-900" data-testid="testimonials-heading">
            Loved by FMCG Businesses
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="testimonial-1">
              <p className="text-lg text-slate-700 leading-relaxed mb-6 italic">
                “TurboPay helped us digitize our entire collections process. We now track every invoice and payment in real time, and our daily reconciliation time has reduced drastically.”
              </p>
              <p className="text-slate-600 font-medium">SLV Distributors, Bangalore</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="testimonial-2">
              <p className="text-lg text-slate-700 leading-relaxed mb-6 italic">
               “Managing field collections was always messy. TurboPay gives us complete visibility of what each salesman collects every day. No more cash mismatches or manual tracking.”
              </p>
              <p className="text-slate-600 font-medium">Silverline Distributors, Bangalore</p>
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-10 md:py-16 bg-gradient-to-br from-indigo-50 to-white" data-testid="final-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-4xl md:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-6" data-testid="final-cta-heading">
            Ready to Unlock Faster Cash Flow?
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="final-cta-subheading">
            Scale your FMCG business with TurboPay.
          </p>
          <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-14 px-10 rounded-full font-semibold text-lg shadow-[0_8px_20px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="final-cta-btn">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {/* <button onClick={() => scrollToSection('solution')} className="hover:text-[#1F497D] transition-colors">
                Product
              </button> */}
              <button onClick={() => scrollToSection('features')} className="hover:text-[#1F497D] transition-colors">
                Solutions
              </button>
              <Link to="/pricing" className="hover:text-[#1F497D] transition-colors">
                Pricing
              </Link>
              <Link to="/faq" className="hover:text-[#1F497D] transition-colors">
                FAQs
              </Link>
              <a href="#" className="hover:text-indigo-400 transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-400">© 2026 TurboPay. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
